import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { CmmdBoardTask, CmmdProject, EnvironmentId, ProjectId } from "@cmmd-forge/contracts";

import type { SidebarThreadSummary } from "~/types";
import { selectSidebarThreadsForProjectRef, useStore } from "~/store";
import type { ForgeExecutionTarget } from "../projects/forgeExecutionTarget";
import { useProjectWorkSessions } from "../projects/useProjectWorkSessions";
import {
  resolveExistingTaskThreadConversation,
  resolveExistingTaskWorkSessionConversation,
} from "../projects/cmmdProjectHomeNavigation";
import { useTaskWorkSessionLauncher } from "./useTaskWorkSessionLauncher";

export interface ChatTaskWorkSessionInput {
  readonly projectId: string;
  readonly project: CmmdProject | null;
  readonly forgeTarget: ForgeExecutionTarget | null;
  readonly tasks: readonly CmmdBoardTask[];
  readonly onSessionStarted: () => void;
}

export interface ChatTaskWorkSession {
  readonly launcher: ReturnType<typeof useTaskWorkSessionLauncher>;
  /** Forge threads for this project, used for the card's task thread history. */
  readonly threadSummaries: readonly SidebarThreadSummary[];
  /** Navigate to a Forge thread listed in that history. */
  readonly openThread: (thread: SidebarThreadSummary) => void;
}

const NO_THREAD_SUMMARIES: readonly SidebarThreadSummary[] = [];

/**
 * Wire the chat task panel into the same work-session launcher the project
 * surface uses, so "Start task" opens one dialog rather than two flows.
 *
 * The panel could not do this in phase 1 because the launcher needs Forge
 * threads, which are keyed by a Forge project ref rather than the CMMD project
 * id this surface starts from. {@link ForgeExecutionTarget} closes that gap —
 * with it resolved, the same store selector the project page uses works here,
 * which is also what finally gives the card its task thread history.
 *
 * A project with no linked Forge project is legitimate: `forgeTarget` is then
 * `null`, thread summaries are empty, and the launcher falls back to resolving
 * an existing conversation from CMMD work sessions alone.
 */
export function useChatTaskWorkSession(input: ChatTaskWorkSessionInput): ChatTaskWorkSession {
  const navigate = useNavigate();
  const workSessions = useProjectWorkSessions(input.projectId);
  const activeEnvironmentId = useStore((state) => state.activeEnvironmentId);
  const environmentStateById = useStore((state) => state.environmentStateById);
  const forgeTarget = input.forgeTarget;

  const threadSummaries = useMemo(() => {
    if (forgeTarget === null) return NO_THREAD_SUMMARIES;
    return selectSidebarThreadsForProjectRef(
      { activeEnvironmentId, environmentStateById },
      scopeProjectRef(
        forgeTarget.environmentId as EnvironmentId,
        forgeTarget.projectId as ProjectId,
      ),
    );
  }, [activeEnvironmentId, environmentStateById, forgeTarget]);

  const launcher = useTaskWorkSessionLauncher({
    project: input.project,
    workspaceName: input.project?.workspaceName ?? null,
    tasks: input.tasks,
    resolveExistingConversation: async (taskId) => {
      // Prefer a live Forge thread; fall back to a recorded work session, which
      // is the only path available when the project has no linked Forge project.
      const liveThread = resolveExistingTaskThreadConversation(threadSummaries, taskId);
      if (liveThread !== null) return liveThread;
      return resolveExistingTaskWorkSessionConversation(await workSessions.reload(), taskId);
    },
    onOpenThread: (target) => {
      void navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: target.environmentId, threadId: target.threadId },
      });
    },
    onSessionStarted: () => {
      void workSessions.reload();
      input.onSessionStarted();
    },
  });

  return {
    launcher,
    threadSummaries,
    openThread: (thread) =>
      void navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: thread.environmentId, threadId: thread.id },
      }),
  };
}
